import { AuthService } from './auth'

export class WhatsAppService {
  static UNIQUE_SESSION_KEY = 'whatsapp_unique_session_id'

  /**
   * Get unique session ID from API and store it
   * GET https://dashboard.quali-d.com/api/meta_get_whatsapp_uniqueid.php?token={token}
   */
  static async getUniqueSessionId () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`https://dashboard.quali-d.com/api/meta_get_whatsapp_uniqueid.php?token=${token}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      console.log('Unique session ID response:', data)

      if (data.unique_session_id) {
        // Store the unique session ID for later use
        localStorage.setItem(this.UNIQUE_SESSION_KEY, data.unique_session_id)
        return {
          success: true,
          uniqueSessionId: data.unique_session_id,
          data: data
        }
      } else {
        throw new Error('No unique session ID received from API')
      }
    } catch (error) {
      console.error('Error getting unique session ID:', error)
      return {
        success: false,
        uniqueSessionId: null,
        error: error.message
      }
    }
  }

  /**
   * Get stored unique session ID
   */
  static getStoredUniqueSessionId () {
    return localStorage.getItem(this.UNIQUE_SESSION_KEY)
  }

  /**
   * Get WhatsApp connection status
   * GET https://qualid.dvps.online/{uniqueSessionID}/status
   */
  static async getConnectionStatus (uniqueSessionId = null) {
    try {
      // Use provided ID or get from storage
      const sessionId = uniqueSessionId || this.getStoredUniqueSessionId()

      if (!sessionId) {
        // Try to get unique session ID first
        const sessionResult = await this.getUniqueSessionId()
        if (!sessionResult.success) {
          throw new Error('Unable to get unique session ID')
        }
        // Use the newly retrieved session ID
        const newSessionId = sessionResult.uniqueSessionId

        return await this.getConnectionStatus(newSessionId)
      }

      console.log('Checking WhatsApp status for session:', sessionId)

      const response = await fetch(`https://qualid.dvps.online/${sessionId}/status`, {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      console.log('WhatsApp status response:', data)

      return {
        success: true,
        status: data.status || 'Unknown',
        uniqueSessionId: sessionId
      }
    } catch (error) {
      console.error('Error getting WhatsApp status:', error)
      return {
        success: false,
        status: 'Error',
        error: error.message
      }
    }
  }

  /**
   * Refresh connection status (recheck)
   */
  static async refreshConnectionStatus () {
    // Clear stored session ID to force refresh
    localStorage.removeItem(this.UNIQUE_SESSION_KEY)

    // Get fresh status
    return await this.getConnectionStatus()
  }

  /**
   * Get status color based on connection status
   */
  static getStatusColor (status) {
    switch (status?.toLowerCase()) {
      case 'connected':
        return '#10b981' // green
      case 'connecting':
        return '#f59e0b' // yellow
      case 'disconnected':
        return '#ef4444' // red
      case 'error':
        return '#ef4444' // red
      default:
        return '#6b7280' // gray
    }
  }

  /**
   * Get status icon based on connection status
   */
  static getStatusIcon (status) {
    switch (status?.toLowerCase()) {
      case 'connected':
        return '✅'
      case 'connecting':
        return '🔄'
      case 'disconnected':
        return '❌'
      case 'error':
        return '⚠️'
      default:
        return '❓'
    }
  }

  /**
   * Initialize WhatsApp connection
   * POST https://qualid.dvps.online/{uniqueSessionID}/initialize
   */
  static async initializeConnection (uniqueSessionId = null) {
    try {
      // Use provided ID or get from storage
      const sessionId = uniqueSessionId || this.getStoredUniqueSessionId()

      if (!sessionId) {
        // Try to get unique session ID first
        const sessionResult = await this.getUniqueSessionId()
        if (!sessionResult.success) {
          throw new Error('Unable to get unique session ID')
        }
        // Use the newly retrieved session ID
        const newSessionId = sessionResult.uniqueSessionId

        return await this.initializeConnection(newSessionId)
      }

      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      console.log('Initializing WhatsApp connection for session:', sessionId)

      const response = await fetch(`https://qualid.dvps.online/${sessionId}/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          QUALID_TOKEN: token
        }),
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      console.log('WhatsApp initialization response:', data)

      return {
        success: true,
        message: data.message || 'WhatsApp initialized successfully',
        uniqueSessionId: sessionId
      }
    } catch (error) {
      console.error('Error initializing WhatsApp connection:', error)
      return {
        success: false,
        message: 'Failed to initialize WhatsApp connection',
        error: error.message
      }
    }
  }

  /**
   * Block a WhatsApp contact
   * POST https://api.quali-d.com/whatsapp-conversations/block
   * Limitation: Can only block users who messaged in the last 24 hours
   */
  static async blockContact (phoneNumber) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      console.log('Blocking WhatsApp contact:', phoneNumber)

      const response = await fetch('https://api.quali-d.com/whatsapp-conversations/block', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          phone_number: phoneNumber
        }),
        mode: 'cors'
      })

      const data = await response.json()
      console.log('Block contact response:', data)

      if (!response.ok || !data.success) {
        // Handle specific error codes
        if (data.whatsapp_response?.error?.code === 131047) {
          throw new Error('Can only block users who messaged in the last 24 hours')
        } else if (data.whatsapp_response?.error?.code === 139101) {
          throw new Error('Blocklist limit reached (max 64,000 contacts)')
        } else {
          throw new Error(data.message || 'Failed to block contact')
        }
      }

      return {
        success: true,
        message: data.message || 'Contact blocked successfully',
        blockedUser: data.blocked_user
      }
    } catch (error) {
      console.error('Error blocking contact:', error)
      return {
        success: false,
        message: error.message || 'Failed to block contact',
        error: error.message
      }
    }
  }

  /**
   * Unblock a WhatsApp contact
   * POST https://api.quali-d.com/whatsapp-conversations/unblock
   */
  static async unblockContact (phoneNumber) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      console.log('Unblocking WhatsApp contact:', phoneNumber)

      const response = await fetch('https://api.quali-d.com/whatsapp-conversations/unblock', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          phone_number: phoneNumber
        }),
        mode: 'cors'
      })

      const data = await response.json()
      console.log('Unblock contact response:', data)

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to unblock contact')
      }

      return {
        success: true,
        message: data.message || 'Contact unblocked successfully',
        unblockedUser: data.unblocked_user
      }
    } catch (error) {
      console.error('Error unblocking contact:', error)
      return {
        success: false,
        message: error.message || 'Failed to unblock contact',
        error: error.message
      }
    }
  }

  /**
   * Get list of blocked contacts
   * GET https://api.quali-d.com/whatsapp-conversations/blocked-list
   */
  static async getBlockedContacts (limit = 20, after = null, before = null) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Build query parameters
      const params = new URLSearchParams()
      params.append('limit', limit)
      if (after) params.append('after', after)
      if (before) params.append('before', before)

      console.log('Getting blocked contacts list')

      const response = await fetch(`https://api.quali-d.com/whatsapp-conversations/blocked-list?${params.toString()}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        },
        mode: 'cors'
      })

      const data = await response.json()
      console.log('Blocked contacts response:', data)

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to retrieve blocked contacts')
      }

      return {
        success: true,
        blockedContacts: data.blocked_contacts || [],
        paging: data.paging || null
      }
    } catch (error) {
      console.error('Error getting blocked contacts:', error)
      return {
        success: false,
        blockedContacts: [],
        paging: null,
        error: error.message
      }
    }
  }
}
